import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEmpresa } from "../context/EmpresaContext";

const MENSEGER_SOUND = "/Menseger.mp3";
const ONLINE_WINDOW_MS = 10 * 60 * 1000;
const API_ROOT = "https://api.captacrm.com.br";
const API_CHAT = `${API_ROOT}/api`;

function lerJsonLocalStorage(chave, fallback = {}) {
  if (typeof window === "undefined") return fallback;
  try {
    return JSON.parse(localStorage.getItem(chave) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
}

function normalizarTexto(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizarBool(v) {
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    String(v || "").toLowerCase() === "true"
  );
}

function getTimestampMs(item) {
  const raw =
    item?.criado_em ||
    item?.created_at ||
    item?.data_envio ||
    item?.data ||
    item?.timestamp ||
    item?.enviado_em ||
    item?.updated_at ||
    null;

  if (!raw) return 0;

  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function formatHora(item) {
  const ms = getTimestampMs(item);
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDataHora(item) {
  const ms = getTimestampMs(item);
  if (!ms) return "";
  return new Date(ms).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function primeiroENome(user) {
  const nome = String(user?.nome || "").trim();
  if (!nome) return "Usuário";
  return nome.split(" ").slice(0, 2).join(" ");
}

function primeiroNome(user) {
  const nome = String(user?.nome || "").trim();
  if (!nome) return "Usuário";
  return nome.split(" ")[0];
}

function coletarMetadadosMensagem(m) {
  const itens = [];

  if (m?.lead_id || m?.id_lead) {
    itens.push(`Lead #${m.lead_id || m.id_lead}`);
  }

  if (m?.lead_nome || m?.nome_lead) {
    itens.push(String(m.lead_nome || m.nome_lead));
  }

  if (m?.tipo) {
    itens.push(`Tipo: ${m.tipo}`);
  }

  if (m?.referencia) {
    itens.push(`Ref: ${m.referencia}`);
  }

  if (m?.origem) {
    itens.push(`Origem: ${m.origem}`);
  }

  return itens;
}

function resolverFotoUsuario(foto) {
  const src = String(foto || "").trim();
  if (!src) return "";

  if (
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("data:") ||
    src.startsWith("blob:")
  ) {
    return src;
  }

  if (src.startsWith("/")) {
    return `${API_ROOT}${src}`;
  }

  return `${API_ROOT}/${src.replace(/^\/+/, "")}`;
}

function montarChaveUsuario(u) {
  const email = normalizarTexto(u?.email);
  const nome = normalizarTexto(u?.nome);
  const id = String(u?.id || "").trim();

  if (email) return `email:${email}`;
  if (nome) return `nome:${nome}`;
  return `id:${id}`;
}

function gerarAliasesUsuario(user) {
  const nome = String(user?.nome || "").trim();
  if (!nome) return [];

  const partes = nome.split(" ").filter(Boolean);
  const aliases = new Set();

  aliases.add(normalizarTexto(nome));
  aliases.add(normalizarTexto(partes.slice(0, 2).join(" ")));
  aliases.add(normalizarTexto(partes[0] || ""));

  return Array.from(aliases).filter(Boolean);
}

function mensagemCombinaComUsuario(mensagem, user) {
  const remetente = normalizarTexto(mensagem?.remetente);
  if (!remetente) return false;

  const aliases = gerarAliasesUsuario(user);
  if (aliases.length === 0) return false;

  return aliases.some((alias) => {
    return (
      remetente === alias ||
      remetente.startsWith(alias) ||
      alias.startsWith(remetente)
    );
  });
}

export default function CaptaChatWidget() {
  const { empresaAtiva } = useEmpresa();

  const usuarioLS = useMemo(() => lerJsonLocalStorage("usuarioLogado", {}), []);
  const empresaAtivaLS = useMemo(
    () => lerJsonLocalStorage("empresaAtiva", {}),
    [],
  );

  const empresaId = String(
    empresaAtiva?.id || empresaAtivaLS?.id || usuarioLS?.empresa_id || "",
  ).trim();

  const usuarioId = usuarioLS?.id;
  const usuarioNome = usuarioLS?.nome || "";
  const usuarioEmail = usuarioLS?.email || "";

  const [chat, setChat] = useState([]);
  const [mensagem, setMensagem] = useState("");
  const [aberto, setAberto] = useState(false);
  const [destinatario, setDestinatario] = useState("todos");
  const [toast, setToast] = useState(null);
  const [naoLidas, setNaoLidas] = useState(0);
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosErro, setUsuariosErro] = useState(false);

  const prevChatRef = useRef([]);
  const chatBottomRef = useRef(null);
  const toastTimerRef = useRef(null);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") || "" : "";

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!empresaId || !token) {
      setUsuarios([]);
      setUsuariosErro(true);
      return;
    }

    let ativo = true;

    const carregarUsuarios = async () => {
      try {
        const resp = await fetch(
          `${API_ROOT}/usuarios?empresa_id=${encodeURIComponent(empresaId)}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!resp.ok) throw new Error(`Erro ${resp.status}`);

        const data = await resp.json();
        if (!ativo) return;

        setUsuarios(Array.isArray(data) ? data : []);
        setUsuariosErro(false);
      } catch (err) {
        console.error("❌ Erro ao buscar usuários do chat:", err);
        if (!ativo) return;
        setUsuarios([]);
        setUsuariosErro(true);
      }
    };

    carregarUsuarios();
    const interval = setInterval(carregarUsuarios, 30000);

    return () => {
      ativo = false;
      clearInterval(interval);
    };
  }, [empresaId, token]);

  useEffect(() => {
    if (!empresaId || !usuarioNome) return;

    let ativo = true;

    const fetchAll = async () => {
      try {
        const [chatResp, naoLidasResp] = await Promise.all([
          fetch(`${API_CHAT}/chat/${empresaId}`),
          fetch(
            `${API_CHAT}/chat/nao-lidas/${empresaId}/${encodeURIComponent(usuarioNome)}`,
          ),
        ]);

        const chatData = chatResp.ok ? await chatResp.json() : [];
        const naoLidasData = naoLidasResp.ok
          ? await naoLidasResp.json()
          : { naoLidas: 0 };

        if (!ativo) return;

        setChat(Array.isArray(chatData) ? chatData : []);
        setNaoLidas(Number(naoLidasData?.naoLidas || 0));
      } catch (err) {
        console.error("❌ Erro ao buscar dados do chat:", err);
        if (!ativo) return;
        setChat([]);
        setNaoLidas(0);
      }
    };

    fetchAll();
    const interval = setInterval(fetchAll, 5000);

    return () => {
      ativo = false;
      clearInterval(interval);
    };
  }, [empresaId, usuarioNome]);

  const usuariosUnicos = useMemo(() => {
    const mapa = new Map();

    for (const u of usuarios || []) {
      const chave = montarChaveUsuario(u);
      const atual = mapa.get(chave);

      if (!atual) {
        mapa.set(chave, u);
        continue;
      }

      const atualTemFoto = !!resolverFotoUsuario(atual?.foto);
      const novoTemFoto = !!resolverFotoUsuario(u?.foto);

      if (!atualTemFoto && novoTemFoto) {
        mapa.set(chave, u);
      }
    }

    return Array.from(mapa.values());
  }, [usuarios]);

  const usuarioBanco = useMemo(() => {
    const emailNorm = normalizarTexto(usuarioEmail);
    const nomeNorm = normalizarTexto(usuarioNome);

    return (
      usuariosUnicos.find((u) => {
        const mesmoId = String(u.id) === String(usuarioId);
        const mesmoEmail =
          !!emailNorm && normalizarTexto(u.email) === emailNorm;
        const mesmoNome = !!nomeNorm && normalizarTexto(u.nome) === nomeNorm;
        return mesmoId || mesmoEmail || mesmoNome;
      }) || null
    );
  }, [usuariosUnicos, usuarioId, usuarioEmail, usuarioNome]);

  const usuariosDestino = useMemo(() => {
    const emailNorm = normalizarTexto(usuarioEmail);
    const nomeNorm = normalizarTexto(usuarioNome);

    return usuariosUnicos
      .filter((u) => {
        const mesmoId = String(u.id) === String(usuarioId);
        const mesmoEmail =
          !!emailNorm && normalizarTexto(u.email) === emailNorm;
        const mesmoNome = !!nomeNorm && normalizarTexto(u.nome) === nomeNorm;

        return !(mesmoId || mesmoEmail || mesmoNome);
      })
      .sort((a, b) =>
        String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"),
      );
  }, [usuariosUnicos, usuarioId, usuarioEmail, usuarioNome]);

  const encontrarUsuarioPorNome = (nome) => {
    const alvo = normalizarTexto(nome);
    if (!alvo) return null;

    return (
      usuariosDestino.find((u) => {
        const aliases = gerarAliasesUsuario(u);
        return aliases.some((alias) => {
          return (
            alias === alvo || alias.startsWith(alvo) || alvo.startsWith(alias)
          );
        });
      }) || null
    );
  };

  const isUsuarioOnline = (user) => {
    if (!user) return false;
    if (String(user.id) === String(usuarioId)) return true;
    if (normalizarBool(user.online)) return true;

    const ultimoAcesso =
      user?.ultimo_acesso || user?.last_seen || user?.updated_at;
    if (ultimoAcesso) {
      const ms = new Date(ultimoAcesso).getTime();
      if (Number.isFinite(ms) && Date.now() - ms <= ONLINE_WINDOW_MS) {
        return true;
      }
    }

    const agora = Date.now();
    const ultimasMensagensRecentes = (chat || [])
      .filter((m) => {
        const ms = getTimestampMs(m);
        return !!ms && agora - ms <= ONLINE_WINDOW_MS;
      })
      .filter((m) => mensagemCombinaComUsuario(m, user));

    return ultimasMensagensRecentes.length > 0;
  };

  useEffect(() => {
    const prevChat = prevChatRef.current;

    if (chat.length > prevChat.length) {
      const ultimaMsg = chat[chat.length - 1];

      const destinatarioEhMeu =
        ultimaMsg?.destinatario === usuarioNome ||
        ultimaMsg?.destinatario === "todos";
      const naoFuiEu = ultimaMsg?.remetente !== usuarioNome;

      if (destinatarioEhMeu && naoFuiEu) {
        const audio = new Audio(MENSEGER_SOUND);
        audio.play().catch((e) => {
          console.warn("Autoplay bloqueado pelo navegador:", e.message);
        });

        setToast({
          texto: ultimaMsg?.texto || "",
          remetente: ultimaMsg?.remetente || "Usuário",
          horario: formatHora(ultimaMsg),
        });

        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 3000);
      }
    }

    prevChatRef.current = chat;
  }, [chat, usuarioNome]);

  useEffect(() => {
    if (aberto && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat, aberto, destinatario]);

  useEffect(() => {
    if (!aberto || !empresaId || !usuarioNome) return;

    fetch(`${API_CHAT}/chat/lidas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empresa_id: empresaId, usuario: usuarioNome }),
    })
      .then(() => setNaoLidas(0))
      .catch((err) =>
        console.error("❌ Erro ao marcar mensagens como lidas:", err),
      );
  }, [aberto, empresaId, usuarioNome]);

  const mensagensVisiveis = useMemo(() => {
    const base = (chat || []).filter(
      (m) => String(m?.empresa_id) === String(empresaId),
    );

    if (destinatario === "todos") {
      return base.filter(
        (m) =>
          m?.destinatario === "todos" ||
          (m?.remetente === usuarioNome && m?.destinatario === "todos"),
      );
    }

    return base.filter((m) => {
      const fuiEuParaEle =
        m?.remetente === usuarioNome && m?.destinatario === destinatario;
      const eleParaMim =
        m?.remetente === destinatario && m?.destinatario === usuarioNome;
      return fuiEuParaEle || eleParaMim;
    });
  }, [chat, empresaId, destinatario, usuarioNome]);

  const destinatarioAtual = useMemo(() => {
    if (destinatario === "todos") return null;
    return (
      usuariosDestino.find((u) => String(u.nome) === String(destinatario)) ||
      encontrarUsuarioPorNome(destinatario) ||
      null
    );
  }, [usuariosDestino, destinatario]);

  const abrirConversaCom = (nomeRemetente) => {
    const usuarioEncontrado = encontrarUsuarioPorNome(nomeRemetente);

    if (usuarioEncontrado?.nome) {
      setDestinatario(usuarioEncontrado.nome);
    } else {
      setDestinatario(nomeRemetente);
    }

    setAberto(true);
    setToast(null);
  };

  const enviarMensagem = async () => {
    if (String(mensagem || "").trim() === "" || !destinatario) return;

    const body = {
      empresa_id: empresaId,
      remetente: usuarioNome,
      destinatario,
      texto: mensagem.trim(),
    };

    try {
      const resp = await fetch(`${API_CHAT}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const novaMsg = await resp.json();
      if (novaMsg && (novaMsg.id || novaMsg.texto)) {
        setChat((c) => [...c, novaMsg]);
        setMensagem("");
        setTimeout(() => {
          if (chatBottomRef.current) {
            chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 80);
      }
    } catch (e) {
      console.error("❌ Erro ao enviar mensagem:", e);
      alert("Erro ao enviar mensagem.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  const renderUsuarioOption = (u) => {
    const nomeExibicao = primeiroENome(u);
    const online = isUsuarioOnline(u);
    return (
      <option key={`${montarChaveUsuario(u)}-${u.id || u.nome}`} value={u.nome}>
        {online ? "🟢 " : "⚪ "}
        {nomeExibicao}
      </option>
    );
  };

  if (!usuarioNome || !empresaId) {
    return (
      <div
        style={{
          position: "fixed",
          bottom: 30,
          right: 30,
          background: "#fff",
          color: "#f00",
          padding: 18,
          borderRadius: 8,
          boxShadow: "0 2px 8px #0002",
          fontWeight: "bold",
          fontSize: 15,
          zIndex: 9999,
        }}
      >
        ⚠️ Usuário não autenticado.
        <br />
        Faça login para usar o chat.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          position: "fixed",
          bottom: 30,
          right: 30,
          zIndex: 9999,
          cursor: "pointer",
        }}
        onClick={() => setAberto((v) => !v)}
        title="Abrir chat"
      >
        <div
          style={{
            background: "linear-gradient(135deg,#2196f3 0%,#3f51b5 100%)",
            color: "#fff",
            borderRadius: "50%",
            width: 54,
            height: 54,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            boxShadow: "0 2px 8px #0003",
            position: "relative",
            border: "4px solid #fff",
          }}
        >
          <span style={{ fontSize: 27, marginTop: 2 }}>💬</span>
          {naoLidas > 0 && (
            <span
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "#3f51b5",
                borderRadius: "50%",
                minWidth: 18,
                height: 18,
                padding: "0 4px",
                color: "#fff",
                fontSize: 11,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #2196f3",
              }}
            >
              {naoLidas}
            </span>
          )}
        </div>
      </div>

      {toast && (
        <div
          onClick={() => abrirConversaCom(toast.remetente)}
          title="Abrir conversa"
          style={{
            position: "fixed",
            bottom: 92,
            right: 44,
            background: "linear-gradient(135deg,#2196f3 0%,#3f51b5 100%)",
            color: "#fff",
            padding: "13px 22px",
            borderRadius: 18,
            boxShadow: "0 4px 20px #0007",
            fontWeight: 500,
            zIndex: 10001,
            animation: "toastIn 0.4s",
            minWidth: 225,
            maxWidth: 300,
            fontSize: 15,
            letterSpacing: 0.1,
            border: "1px solid #2196f3",
            cursor: "pointer",
          }}
        >
          <div style={{ marginBottom: 2, color: "#fff", fontWeight: 700 }}>
            {toast.remetente}
          </div>
          <div style={{ fontWeight: 400 }}>{toast.texto}</div>
          {!!toast.horario && (
            <div style={{ marginTop: 4, fontSize: 11, opacity: 0.9 }}>
              {toast.horario}
            </div>
          )}
        </div>
      )}

      {aberto && (
        <div
          style={{
            position: "fixed",
            bottom: 100,
            right: 30,
            width: 390,
            height: 540,
            background: "#f5f7fa",
            borderRadius: 18,
            boxShadow: "0 6px 32px #0004",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: "Roboto,Segoe UI,Arial,sans-serif",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg,#2196f3 0%,#3f51b5 100%)",
              color: "#fff",
              padding: "13px 16px 11px 16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {resolverFotoUsuario(usuarioBanco?.foto) ? (
              <img
                src={resolverFotoUsuario(usuarioBanco?.foto)}
                alt={usuarioNome}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginRight: 14,
                  border: "2px solid #fff",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  background: "#e1e1e1",
                  color: "#2196f3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 18,
                  marginRight: 14,
                  border: "2px solid #fff",
                }}
              >
                {usuarioNome
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                flex: 1,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 16, marginBottom: 1 }}>
                {usuarioNome}
              </span>
              {usuarioEmail && (
                <span
                  style={{ fontWeight: 400, fontSize: 12, color: "#dbeafe" }}
                >
                  {usuarioEmail}
                </span>
              )}
            </div>

            <span
              style={{
                cursor: "pointer",
                fontSize: 25,
                marginLeft: 12,
                lineHeight: "1",
                color: "#fff",
                opacity: 0.8,
              }}
              onClick={() => setAberto(false)}
              title="Fechar"
            >
              ×
            </span>
          </div>

          <div
            style={{
              padding: "10px 13px 9px 13px",
              borderBottom: "1px solid #eef2f6",
              background: "#e3eafc",
            }}
          >
            <label style={{ fontSize: 13, color: "#2e2e2e", display: "block" }}>
              <span style={{ marginRight: 6 }}>Para:</span>
              <select
                style={{
                  marginLeft: 0,
                  padding: "6px 9px",
                  borderRadius: 8,
                  border: "1px solid #b3c1e1",
                  fontSize: 14,
                  background: "#fff",
                  outline: "none",
                  width: "100%",
                  marginTop: 6,
                }}
                value={destinatario}
                onChange={(e) => setDestinatario(e.target.value)}
              >
                <option value="todos">📣 Todos</option>
                {usuarios.length === 0 && !usuariosErro && (
                  <option disabled>Carregando usuários...</option>
                )}
                {usuariosErro && (
                  <option disabled>Não foi possível carregar usuários</option>
                )}
                {usuariosDestino.map((u) => renderUsuarioOption(u))}
              </select>
            </label>

            {destinatarioAtual && (
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  color: "#334155",
                  background: "#ffffffb3",
                  border: "1px solid #bfdbfe",
                  borderRadius: 10,
                  padding: "7px 10px",
                }}
              >
                {resolverFotoUsuario(destinatarioAtual?.foto) ? (
                  <img
                    src={resolverFotoUsuario(destinatarioAtual?.foto)}
                    alt={destinatarioAtual?.nome}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "1px solid #cbd5e1",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : null}

                <span
                  style={{
                    color: isUsuarioOnline(destinatarioAtual)
                      ? "#16a34a"
                      : "#94a3b8",
                  }}
                >
                  ●
                </span>
                <span style={{ fontWeight: 600 }}>
                  {primeiroENome(destinatarioAtual)}
                </span>
                <span style={{ color: "#64748b" }}>
                  {isUsuarioOnline(destinatarioAtual) ? "Online" : "Offline"}
                </span>
              </div>
            )}
          </div>

          <div
            style={{
              flex: 1,
              padding: "15px 10px 6px 10px",
              overflowY: "auto",
              background: "#f5f7fa",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {mensagensVisiveis.length === 0 && (
              <div
                style={{ color: "#888", textAlign: "center", marginTop: 40 }}
              >
                Nenhuma mensagem ainda.
              </div>
            )}

            {mensagensVisiveis.map((m, idx) => {
              const minhas = m?.remetente === usuarioNome;
              const metadados = coletarMetadadosMensagem(m);
              const horario = formatHora(m);
              const dataHora = formatDataHora(m);

              return (
                <div
                  key={m.id || idx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: minhas ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      display: "inline-block",
                      padding: "8px 14px",
                      background: minhas
                        ? "linear-gradient(135deg,#2196f3 0%,#3f51b5 100%)"
                        : "#fff",
                      color: minhas ? "#fff" : "#222",
                      borderRadius: minhas
                        ? "15px 15px 4px 15px"
                        : "15px 15px 15px 4px",
                      fontSize: 15,
                      maxWidth: "84%",
                      wordBreak: "break-word",
                      margin: "2px 0 0 0",
                      boxShadow: minhas
                        ? "0 1px 2px #b7c6e6aa"
                        : "0 1px 2px #bbb",
                    }}
                    title={dataHora || undefined}
                  >
                    <div>{m?.texto}</div>

                    {metadados.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        {metadados.map((item, i) => (
                          <span
                            key={`${m.id || idx}-meta-${i}`}
                            style={{
                              fontSize: 11,
                              padding: "3px 8px",
                              borderRadius: 999,
                              background: minhas
                                ? "rgba(255,255,255,0.18)"
                                : "#dbeafe",
                              color: minhas ? "#eff6ff" : "#1e3a8a",
                            }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b6b6b",
                      marginTop: 2,
                      marginLeft: 6,
                      marginRight: 6,
                      marginBottom: 6,
                      alignSelf: minhas ? "flex-end" : "flex-start",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{m?.remetente}</span>
                    {horario && <span>{horario}</span>}
                  </div>
                </div>
              );
            })}
            <div ref={chatBottomRef} />
          </div>

          <div
            style={{
              padding: "8px 10px 8px 10px",
              background: "#e3eafc",
              display: "flex",
              alignItems: "center",
              borderTop: "1px solid #eef2f6",
            }}
          >
            <input
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 22,
                border: "1px solid #b3c1e1",
                fontSize: 15,
                background: "#fff",
                outline: "none",
                marginRight: 8,
                boxShadow: "0 1px 2px #eee",
              }}
              type="text"
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                destinatario === "todos"
                  ? "Mensagem para todos..."
                  : `Mensagem para ${primeiroNome(destinatarioAtual)}...`
              }
              autoFocus
            />
            <button
              style={{
                width: 44,
                height: 44,
                background: "linear-gradient(135deg,#2196f3 0%,#3f51b5 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                boxShadow: "0 2px 5px #2196f333",
              }}
              onClick={enviarMensagem}
              title="Enviar"
              type="button"
            >
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                <path d="M3 21l18-9L3 3v7l15 2-15 2v7z" fill="#fff" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        div[style*='overflow-y: auto']::-webkit-scrollbar {
          width: 7px;
        }
        div[style*='overflow-y: auto']::-webkit-scrollbar-thumb {
          background: #2196f344;
          border-radius: 8px;
        }
        input[type="text"]:focus {
          border-color: #2196f3;
        }
        @media (max-width: 500px) {
          div[style*="position: fixed"][style*="width: 390px"] {
            width: 99vw !important;
            right: 0 !important;
            left: 0 !important;
            border-radius: 0 !important;
            height: 100dvh !important;
            bottom: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
